// src/mockData/strategyIntelligenceBriefMock.ts

export type StrategyBriefDimension = {
  id: string;
  label: string;
  score: number;
};

export type StrategyBriefQuadrantPoint = {
  id: string;
  label: string;
  x: number; // Value Potential (0-100)
  y: number; // Execution Readiness (0-100)
  category?: 'Quick Wins' | 'Strategic Investments' | 'Efficiency Plays' | 'Resource Drains';
};

export type StrategyBriefOpportunity = {
  id: string;
  title: string;
  systemInsight: string;
  implication: string;
  opportunity: string;
};

export type StrategyBriefCTA = {
  headline: string;
  supportingCopy: string;
  buttonLabel: string;
};

export type StrategyBriefPreviewBlock = {
  id: string;
  title: string;
  description: string;
};

export type StrategyBriefOpportunitySignal = {
  statement: string;
  signals: Array<{ id: string; label: string; description: string }>;
};

export type StrategyBriefInterpretationGuide = {
  shows: string[];
  howToUse: string[];
  audience: Array<{ role: string; useCase: string }>;
};

export type StrategyIntelligenceBriefMock = {
  title: string;
  subtitle: string;
  clientName: string;
  preparedDate: string;
  executiveSummary: string;

  opportunitySignal: StrategyBriefOpportunitySignal;
  interpretationGuide: StrategyBriefInterpretationGuide;

  recognitionMoment: string;

  score: {
    value: number;
    outOf: number;
    label: string;
  };

  dimensions: StrategyBriefDimension[];
  scoreInterpretation: string;

  quadrant: {
    xLabel: string;
    yLabel: string;
    points: StrategyBriefQuadrantPoint[];
    keyInsight: string;
  };

  opportunities: {
    title: string;
    items: StrategyBriefOpportunity[];
  };

  interpretation: string;

  notIncluded: {
    title: string;
    items: string[];
  };

  futurePreview: {
    title: string;
    blocks: StrategyBriefPreviewBlock[];
  };

  cta: StrategyBriefCTA;
};

export const strategyIntelligenceBriefMock: StrategyIntelligenceBriefMock = {
  title: 'Strategy Intelligence Brief',
  subtitle: 'Opportunity Analysis of Current Strategy Performance',
  clientName: 'Sample Health System',
  preparedDate: 'March 2026',

  executiveSummary:
    'Your current strategy demonstrates strong alignment to enterprise priorities and reflects a thoughtful set of initiatives. There is a meaningful opportunity to unlock additional value through more structured prioritization, stronger ROI visibility, and clearer trade-off evaluation.',

  opportunitySignal: {
    statement:
      'Three structural gaps are constraining how quickly your strategy translates into funded, board-approved execution.',
    signals: [
      {
        id: 'sig1',
        label: 'Prioritization Gap',
        description: 'No unified scoring model across value, readiness, and capacity',
      },
      {
        id: 'sig2',
        label: 'ROI Visibility Gap',
        description: 'Funding decisions rely on advocacy rather than verifiable evidence',
      },
      {
        id: 'sig3',
        label: 'Trade-Off Gap',
        description: 'Portfolio sequencing lacks a consistent decision framework',
      },
    ],
  },

  interpretationGuide: {
    shows: [
      'A strategy effectiveness score across four dimensions',
      'How your initiatives distribute across value and readiness',
      'The top structural gaps constraining execution speed',
      'A clear path to what unlocks next',
    ],
    howToUse: [
      'Use the score to anchor a conversation about execution readiness',
      'Use the quadrant to identify misaligned or stalled initiatives',
      'Use the opportunity cards to frame prioritization decisions',
      'Share with finance and operations to align on next steps',
    ],
    audience: [
      { role: 'CDO', useCase: 'Validate data and analytics strategy sequencing' },
      { role: 'CFO', useCase: 'Assess ROI visibility and funding readiness' },
      { role: 'COO', useCase: 'Evaluate execution capacity and portfolio sequencing' },
    ],
  },

  recognitionMoment:
    'Current prioritization appears to rely on a mix of strategic alignment and stakeholder influence, without a consistent value-based framework.',

  score: {
    value: 72,
    outOf: 100,
    label: 'Strategy Effectiveness Score',
  },

  dimensions: [
    { id: 'alignment', label: 'Strategic Alignment', score: 82 },
    { id: 'roi', label: 'ROI Clarity', score: 58 },
    { id: 'feasibility', label: 'Implementation Feasibility', score: 75 },
    { id: 'readiness', label: 'Data Readiness', score: 70 },
  ],

  scoreInterpretation:
    'Your strategy is directionally strong, but performance may be constrained by inconsistent prioritization, limited visibility into ROI, and lack of a standardized decision framework.',

  quadrant: {
    xLabel: 'Value Potential',
    yLabel: 'Execution Readiness',
    points: [
      { id: 'p1', label: 'RCM AI', x: 80, y: 86, category: 'Quick Wins' },
      { id: 'p2', label: 'Capacity Flow', x: 74, y: 78, category: 'Quick Wins' },
      { id: 'p6', label: 'Clinical Pathways', x: 70, y: 73, category: 'Quick Wins' },

      { id: 'p9', label: 'Staffing Forecasting', x: 46, y: 74, category: 'Strategic Investments' },
      { id: 'p10', label: 'Market Benchmarking', x: 42, y: 68, category: 'Strategic Investments' },
      { id: 'p11', label: 'Access Growth', x: 48, y: 62, category: 'Strategic Investments' },

      { id: 'p3', label: 'Contract Modeling', x: 72, y: 57, category: 'Efficiency Plays' },
      { id: 'p4', label: 'Supply Optimization', x: 66, y: 54, category: 'Efficiency Plays' },
      { id: 'p5', label: 'Service Line Analytics', x: 79, y: 46, category: 'Efficiency Plays' },
      { id: 'p7', label: 'Precision Med', x: 86, y: 39, category: 'Efficiency Plays' },

      { id: 'p8', label: 'Value-Based Ops', x: 54, y: 43, category: 'Resource Drains' },
    ],
    keyInsight: 'Your strategy is aligned — but value is unevenly distributed across initiatives.',
  },

  opportunities: {
    title: 'Top Opportunities to Improve Strategy Performance',
    items: [
      {
        id: 'o1',
        title: 'Standardize Prioritization',
        systemInsight:
          'Initiatives are not evaluated using a unified model across value, readiness, and capacity.',
        implication:
          'Lower-return work advances while high-value initiatives stall — consuming capacity without proportional return.',
        opportunity:
          'Deploy a unified scoring model so the right initiatives advance at the right time.',
      },
      {
        id: 'o2',
        title: 'Strengthen ROI Visibility',
        systemInsight:
          'ROI signals exist but are not consistently framed in a CFO-verifiable model with assumptions and time-to-value.',
        implication:
          'Approvals stall and funding relies on advocacy rather than auditable financial evidence.',
        opportunity:
          'Build CFO-ready ROI models to accelerate approvals and strengthen finance alignment.',
      },
      {
        id: 'o3',
        title: 'Make Trade-Offs Explicit',
        systemInsight:
          'Competing initiatives are not compared within a single view that surfaces cost, capacity, and upside.',
        implication:
          'Implicit trade-offs create internal friction and slow sequencing decisions.',
        opportunity:
          'Surface trade-offs in a unified decision canvas to accelerate capital allocation.',
      },
    ],
  },

  interpretation:
    'Your organization is well-positioned to unlock additional value by improving how decisions are made within the strategy — not by changing the strategy itself.',

  notIncluded: {
    title: 'What You Unlock Next',
    items: [
      'A ranked initiative portfolio with rationale and scoring',
      'Quantified ROI with CFO-ready assumptions and ranges',
      'An execution roadmap sequenced to organizational capacity',
      'A funding-ready decision framework for board approval',
    ],
  },

  futurePreview: {
    title: "What You'll See Next",
    blocks: [
      {
        id: 'fp1',
        title: 'Ranked Initiative Portfolio',
        description: 'A prioritized shortlist with rationale, confidence, and governance-ready scoring.',
      },
      {
        id: 'fp2',
        title: 'ROI & Payback Modeling',
        description: 'Quantified value ranges with assumptions, sensitivity, and CFO-ready evidence.',
      },
      {
        id: 'fp3',
        title: 'Execution Roadmap',
        description: 'Sequencing aligned to capacity, dependencies, and time-to-value.',
      },
    ],
  },

  cta: {
    headline: 'Turn This Analysis Into a Prioritized Roadmap',
    supportingCopy:
      'Get a ranked initiative portfolio, quantified ROI models, and an execution roadmap built for board-level approval.',
    buttonLabel: 'Unlock the Full Analysis',
  },
};
